import { useState } from "react";

import { Callout } from "@blueprintjs/core";

import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import { Box } from "@/components/layout/flexbox";
import { InviteFormUI } from "@/components/pages_logged_in/workspace-admin/InviteFormUI";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import {
  CreateProgramUsersMutation,
  InviteClientsMutationVariables,
  ProgramComponentsQueryDocument,
  ProgramQueryDocument,
  ProgramUserRole,
  ProgramUsersQueryDocument,
  useCreateProgramUsersMutation,
  useWorkspaceSubscriptionQuery,
} from "@/graphql";
import { MainProgram } from "@/types/programs";

interface InviteProgramParticipantsDialogProps {
  program: MainProgram;
  open: boolean;
  afterAddOrUpdateSuccess: () => void;
  onClose: () => void;
  title?: string;
  buttonText?: string;
  scheduleText?: string;
  role?: ProgramUserRole;
  currentWorkspaceCredits?: string | null;
}

export function InviteProgramParticipantsDialog({
  open,
  afterAddOrUpdateSuccess,
  onClose,
  title = "Enroll Participants",
  buttonText = "Add Participants",
  scheduleText,
  role = ProgramUserRole.Participant,
  currentWorkspaceCredits,
  program,
}: InviteProgramParticipantsDialogProps) {
  const [result, setResult] =
    useState<CreateProgramUsersMutation["createProgramUsers"]>();
  const isAdmin = isWorkspaceAdmin();
  const programId = program.id;

  const onError = (error: Error) => {
    setResult({
      ok: false,
      msg: error?.message || "Something went wrong. Please try again.",
    });
  };

  const [createProgramUsers, { loading }] = useCreateProgramUsersMutation({
    onCompleted: (data) => {
      const { ok, msg } = data?.createProgramUsers || {};
      if (ok) {
        setResult(data?.createProgramUsers);
        afterAddOrUpdateSuccess();
      } else {
        onError(new Error(msg ?? undefined));
      }
    },
    onError,
    refetchQueries: [
      {
        query: ProgramComponentsQueryDocument,
        variables: {
          programId,
        },
      },
      {
        query: ProgramQueryDocument,
        variables: {
          programId,
        },
      },
      {
        query: ProgramUsersQueryDocument,
        variables: {
          programId,
        },
      },
    ],
  });

  const { data: workspaceSubscriptionData } = useWorkspaceSubscriptionQuery({
    fetchPolicy: "network-only",
  });

  const planSeats =
    workspaceSubscriptionData?.currentWorkspace?.subscription?.quantity;

  const { numMembers: occupiedSeats, totalPendingInvitations } =
    workspaceSubscriptionData?.currentWorkspace || {};

  const handleAddParticipants = (inviteData: {
    variables: InviteClientsMutationVariables;
  }) => {
    const emails = inviteData?.variables?.emails.split(",");
    const credit = inviteData?.variables.credit;
    const groupId = inviteData?.variables.groupId;

    createProgramUsers({
      variables: { programId, emails, role, groupId, credit: credit ?? "0.00" },
    });
  };

  const handleClose = () => {
    setResult(undefined);
    onClose();
  };

  const errorText = result?.msg || "Something went wrong";

  return (
    <StyledDialog
      isOpen={open}
      lazy={true}
      title={<Box fontSize={18}>{title}</Box>}
      width="480px"
      onClose={handleClose}
      onOpening={() => {
        setResult(undefined);
      }}
      canOutsideClickClose={false}
    >
      <Box
        py={24}
        px={2}
        backgroundColor="#FFF"
        css={{ borderRadius: "0 0 12px 12px" }}
      >
        {result && !result.ok && (
          <Box width={1} p={"0 16px"} mb={2}>
            <Callout icon={null} intent="danger">
              {errorText}
            </Callout>
          </Box>
        )}
        <InviteFormUI
          programId={programId}
          inline={true}
          allowSetCredits={isAdmin}
          allowSetGroup={isAdmin}
          workspaceCredit={currentWorkspaceCredits}
          result={result}
          onUserInvite={handleAddParticipants}
          onInvitationScheduleUpdate={() => {}}
          isLoading={loading}
          allowSchedule={false}
          buttonText={buttonText}
          scheduleText={scheduleText}
          allowAssignCoaches={false}
          planSeats={planSeats}
          occupiedSeats={occupiedSeats}
          totalPendingInvitations={totalPendingInvitations}
        />
      </Box>
    </StyledDialog>
  );
}
